import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLeads, Lead, LeadAction, Appointment } from '../contexts/LeadsContext';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  Phone,
  Email,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  History,
  AccessTime,
  FileDownload,
  Delete,
} from '@mui/icons-material';

interface User {
  id: string;
  name: string;
}

interface AddressOption {
  postcode: string;
  postTown: string;
  address1: string;
  county: string;
}

interface Introducer {
  id: number;
  name: string;
  role: string;
}

const sourceOptions = [
  'Website',
  'Referral',
  'Phone',
  'Facebook',
  'Coupon Magazine',
  'Other Magazines',
  'Returning',
  'Unknown',
  'Passing',
  'Van',
  'Tiktok',
  'Instagram',
  'Bolton Radio',
  'Red Rose'
];
const actionTypes = ['Call', 'Quote', 'Appointment Set', 'Visit Booked', 'Won', 'Lost', 'Update'];
const users: User[] = [
  { id: '22', name: 'John Derbyshire' },  // Database ID: 22
  { id: '21', name: 'Tony Gordon' },      // Database ID: 21
  { id: '23', name: 'Wanda Gordon' }      // Database ID: 23 (stored as "Wanda Wanda" in DB)
];

export default function Leads() {
  const { addLead, updateLead, addAction, addAppointment, refreshLeads } = useLeads();
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('leadFilterSearch') || '';
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    return sessionStorage.getItem('leadFilterStatus') || 'all';
  });
  const [sourceFilter, setSourceFilter] = useState<string>(() => {
    return sessionStorage.getItem('leadFilterSource') || 'all';
  });
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [quickCallDialogOpen, setQuickCallDialogOpen] = useState(false);
  const [quickCallDescription, setQuickCallDescription] = useState('');
  const [quickCallLead, setQuickCallLead] = useState<Lead | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(() => {
    const savedMonth = sessionStorage.getItem('leadFilterMonth');
    if (savedMonth !== null) {
      return parseInt(savedMonth);
    }
    return new Date().getMonth() + 1; // Current month (1-12)
  });

  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const savedYear = sessionStorage.getItem('leadFilterYear');
    if (savedYear !== null) {
      return parseInt(savedYear);
    }
    return new Date().getFullYear(); // Current year
  });
  const [addActionDialogOpen, setAddActionDialogOpen] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [newAction, setNewAction] = useState<Partial<LeadAction>>({
    action_type: 'Call',
    description: '',
    action_date: new Date().toISOString().split('T')[0],
    action_time: new Date().toTimeString().slice(0, 5),
  });
  const [appointment, setAppointment] = useState({
    date: '',
    time: '',
    notes: '',
    type: 'Home Visit',
    assignedUser: '21'  // Tony Gordon's database ID
  });
  const [postcodeDialogOpen, setPostcodeDialogOpen] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [introducers, setIntroducers] = useState<Introducer[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [leadMode, setLeadMode] = useState<'new' | 'existing'>('new');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);


  // Status options from database
  const [statusOptions, setStatusOptions] = useState<Array<{id: number, value: string, label: string, color: string}>>([]);

  // Get current user from auth context
  const currentUser = { firstName: 'Current', lastName: 'User' };
  
  // Fetch status options from database
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await axios.get('/api/leads/statuses');
        setStatusOptions(response.data);
      } catch (error) {
        console.error('Error fetching status options:', error);
        // Fallback to hardcoded statuses if API fails
        setStatusOptions([
          { id: 1, value: 'new', label: 'New', color: '#2196f3' },
          { id: 2, value: 'no_reply', label: 'No Reply', color: '#9e9e9e' },
          { id: 3, value: 'not_ready', label: 'Not Ready', color: '#607d8b' },
          { id: 4, value: 'contacted', label: 'Contacted', color: '#ff9800' },
          { id: 5, value: 'quoted', label: 'Quoted', color: '#3f51b5' },
          { id: 6, value: 'won', label: 'Won', color: '#4caf50' },
          { id: 8, value: 'lost', label: 'Lost', color: '#f44336' },
        ]);
      }
    };
    fetchStatuses();
  }, []);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('leadFilterSearch', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    sessionStorage.setItem('leadFilterStatus', statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    sessionStorage.setItem('leadFilterSource', sourceFilter);
  }, [sourceFilter]);

  // Fetch leads from API on component mount and when page gains focus
  useEffect(() => {
    fetchLeads();
    fetchIntroducers();
    
    // Refresh data when page gains focus (e.g., returning from edit page)
    const handleFocus = () => {
      fetchLeads();
    };
    
    // Add event listener for when the page/tab gains focus
    window.addEventListener('focus', handleFocus);
    
    // Also refresh when navigating back to this page using visibility API
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchLeads();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Also refresh when navigating to this page from another route
  useEffect(() => {
    // This will trigger when the location changes to this page
    fetchLeads();
  }, [location.pathname]);

  const fetchIntroducers = async () => {
    try {
      const response = await fetch('/api/users/non-admin', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setIntroducers(data);
      }
    } catch (error) {
      console.error('Error fetching introducers:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/leads?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform API data to match Lead interface
        const transformedLeads = data.leads.map((lead: any) => ({
          id: lead.id,
          customer_id: lead.customer_id,
          title: lead.title,
          customer_name: lead.customer_name || '',
          first_name: lead.first_name || '',
          last_name: lead.last_name || '',
          email: lead.email || '',
          phone: lead.phone || '',
          home_phone: lead.home_phone || '',
          address_1: lead.customer_address || '',
          address_2: lead.customer_address_2 || '',
          address_3: lead.customer_address_3 || '',
          town: lead.customer_city || '',
          postcode: lead.postcode || '',
          source: lead.source || 'Website',
          status: lead.status || 'new',
          lost_status: lead.lost_status,
          priority: lead.priority || 'medium',
          estimated_value: parseFloat(lead.estimated_value) || 0,
          created_at: lead.created_at,
          next_follow_up: lead.next_follow_up || '',
          order_number: lead.order_number || '',
          introducer_id: lead.introducer_id || null,
          actions: [],
          notes: lead.notes || ''
        }));
        setLeads(transformedLeads);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Postcode lookup function
  const handlePostcodeLookup = async () => {
    if (!postcodeSearch.trim()) {
      alert('Please enter a postcode');
      return;
    }

    setLoadingAddresses(true);
    try {
      // Remove spaces and convert to uppercase
      const cleanPostcode = postcodeSearch.replace(/\s+/g, '').toUpperCase();
      
      // Use the new server-side endpoint
      const response = await axios.get(`/api/postcode/lookup/${cleanPostcode}`);
      
      const data = response.data;
      
      if (data.success && data.addresses && data.addresses.length > 0) {
        setAddressOptions(data.addresses);
      } else if (data.success && data.addresses.length === 0) {
        alert('No addresses found for this postcode');
        setAddressOptions([]);
      } else {
        alert(data.message || 'Error fetching addresses. Please try again.');
        setAddressOptions([]);
      }
    } catch (error: any) {
      console.error('Error fetching addresses:', error);
      const errorMessage = error.response?.data?.message || 'Error fetching addresses. Please try again.';
      alert(errorMessage);
      setAddressOptions([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleAddressSelect = async (address: AddressOption) => {
    if (selectedLead) {
      
      let latitude = null;
      let longitude = null;
      
      // Use postcodes.io API to get coordinates from postcode
      try {
        // Clean the postcode - remove spaces for the API call
        const cleanPostcode = address.postcode.replace(/\s+/g, '');
        const geocodeUrl = `https://api.postcodes.io/postcodes/${cleanPostcode}`;
        
        const response = await fetch(geocodeUrl, {
          headers: {
            'Accept': 'application/json',
          }
        });
        
        
        if (response.ok) {
          const data = await response.json();
          
          if (data && data.status === 200 && data.result) {
            latitude = data.result.latitude;
            longitude = data.result.longitude;
          } else {
            // Don't show alert - coordinates are optional
          }
        } else {
          console.error('❌ Postcodes.io response not OK:', response.status);
          // Try server-side fallback
          try {
            const serverResponse = await axios.get(`/api/postcode/coordinates/${cleanPostcode}`);
            if (serverResponse.data.success) {
              latitude = serverResponse.data.latitude;
              longitude = serverResponse.data.longitude;
            }
          } catch (serverError) {
            console.error('Server-side lookup also failed:', serverError);
          }
        }
      } catch (error) {
        console.error('❌ Error getting postcode coordinates:', error);
        // Try server-side fallback
        try {
          const cleanPostcode = address.postcode.replace(/\s+/g, '');
          const serverResponse = await axios.get(`/api/postcode/coordinates/${cleanPostcode}`);
          if (serverResponse.data.success) {
            latitude = serverResponse.data.latitude;
            longitude = serverResponse.data.longitude;
          }
        } catch (serverError) {
          console.error('Server-side lookup also failed:', serverError);
          // Don't show alert - coordinates are optional, address will still be saved
        }
      }
      
      
      // Create updated lead object with address fields and coordinates
      const updatedLead = {
        ...selectedLead,
        address_1: address.address1,  // e.g. "1 Patterdale Close"
        address_2: '',  // Clear for manual entry
        address_3: address.county || '',  // Use empty string if county is undefined
        town: address.postTown,  // e.g. "PRESTON"
        postcode: address.postcode,  // e.g. "PR1 5TG"
        latitude: latitude,
        longitude: longitude
      };
      
      // Update the selected lead state directly to ensure immediate display
      setSelectedLead(updatedLead);
      
      // If this is editing an existing lead, also update via context
      if (!addDialogOpen) {
        updateLead(selectedLead.id, {
          address_1: address.address1,
          address_2: '',
          address_3: address.county || '',
          town: address.postTown,
          postcode: address.postcode,
          latitude: latitude,
          longitude: longitude
        });
      }
      
      setPostcodeDialogOpen(false);
      setPostcodeSearch('');
      setAddressOptions([]);
    }
  };

  const handleOpenPostcodeLookup = () => {
    setPostcodeSearch('');
    setAddressOptions([]);
    setPostcodeDialogOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredLeads = leads.filter(lead => {
    // Filter by selected month and year (created_at)
    const leadDate = new Date(lead.created_at);

    // Month and year filters work independently
    const matchesMonth = selectedMonth === 0 || leadDate.getMonth() === (selectedMonth - 1);
    const matchesYear = selectedYear === 0 || leadDate.getFullYear() === selectedYear;

    const matchesSearch =
      lead.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      (lead.postcode && lead.postcode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (lead.order_number && lead.order_number.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || (lead.source && lead.source.toLowerCase().replace(/_/g, ' ') === sourceFilter.toLowerCase().replace(/_/g, ' '));

    return matchesMonth && matchesYear && matchesSearch && matchesStatus && matchesSource;
  }).sort((a, b) => {
    // Define status order priority
    const statusOrder: { [key: string]: number } = {
      'new': 1,
      'no_reply': 2,
      'contacted': 3,
      'quoted': 4,
      'not_ready': 5,
      'won': 6,
      'lost': 7
    };
    
    // First sort by status order if no specific sort field is selected
    if (!sortField) {
      const aOrder = statusOrder[a.status] || 999;
      const bOrder = statusOrder[b.status] || 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      
      // Then sort by created_at (newest first) within same status
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    
    // If sorting by status specifically, always use the custom order (never alphabetical)
    if (sortField === 'status') {
      const aOrder = statusOrder[a.status] || 999;
      const bOrder = statusOrder[b.status] || 999;
      // Always use the custom order, not alphabetical
      // 'asc' = New → Contacted → Quoted → Won → Lost
      // 'desc' = Lost → Won → Quoted → Contacted → New
      return sortDirection === 'asc' ? aOrder - bOrder : bOrder - aOrder;
    }
    
    // For other fields, apply normal sorting
    let aVal: any = a[sortField as keyof Lead];
    let bVal: any = b[sortField as keyof Lead];
    
    if (sortField === 'estimated_value') {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      new: 'primary',
      no_reply: 'default',
      not_ready: 'warning',
      contacted: 'secondary',
      quoted: 'warning',
      won: 'success',
      lost: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      high: 'error',
      medium: 'warning',
      low: 'default',
    };
    return colors[priority] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const isOverdue = (date: string) => {
    if (!date || date === 'null' || date === '') return false;
    return new Date(date) < new Date();
  };

  const handleLeadClick = (lead: Lead, autoAction?: { type: 'Call' | 'Visit', description: string }) => {
    // Navigate to the edit page with state to trigger refresh on return
    navigate(`/leads/${lead.id}/edit`, { state: { from: 'leads-list' } });
  };

  const handlePhoneClick = (lead: Lead, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setQuickCallLead(lead);
    setQuickCallDescription('');
    setQuickCallDialogOpen(true);
  };

  const handleQuickCallSave = async () => {
    if (quickCallLead && quickCallDescription) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/actions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lead_id: quickCallLead.id,
            customer_id: quickCallLead.customer_id || null,
            action_type: 'Call',
            description: quickCallDescription,
            action_date: new Date().toISOString().split('T')[0],
            action_time: new Date().toTimeString().slice(0, 5),
            completed: true
          }),
        });

        if (response.ok) {
          const data = await response.json();
          
          // Refresh leads data to show the new action
          await fetchLeads();
          await refreshLeads();
          
          // Close dialog and reset
          setQuickCallDialogOpen(false);
          setQuickCallDescription('');
          setQuickCallLead(null);
        } else {
          const errorData = await response.json();
          console.error('Failed to save call action:', errorData.message);
          alert('Failed to save call description. Please try again.');
        }
      } catch (error) {
        console.error('Error saving call action:', error);
        alert('Error saving call description. Please try again.');
      }
    }
  };

  const handleVisitClick = (lead: Lead, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    navigate(`/leads/${lead.id}/edit`);
  };

  const handleCalendarClick = (lead: Lead, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setSelectedLead(lead);
    
    // Set default appointment date to 3 days from now
    const appointmentDate = new Date();
    appointmentDate.setDate(appointmentDate.getDate() + 3);
    
    setAppointment({
      date: appointmentDate.toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      notes: '',
      type: 'Home Visit',
      assignedUser: '21'  // Tony Gordon's database ID
    });
    
    setAppointmentDialogOpen(true);
  };

  const handleDeleteClick = (lead: Lead, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setLeadToDelete(lead);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (leadToDelete) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/leads/${leadToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          // Remove the lead from local state
          setLeads(prev => prev.filter(lead => lead.id !== leadToDelete.id));
          // Also refresh from context
          await fetchLeads();
          await refreshLeads();
          setDeleteDialogOpen(false);
          setLeadToDelete(null);
        } else {
          const errorData = await response.json();
          alert(`Failed to delete lead: ${errorData.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting lead:', error);
        alert('Error deleting lead. Please try again.');
      }
    }
  };


  const handleFieldChange = (field: keyof Lead, value: any) => {
    if (selectedLead) {
      let updatedLead = { ...selectedLead, [field]: value };

      // Automatically update customer_name when first_name or last_name changes
      if (field === 'first_name' || field === 'last_name') {
        const firstName = field === 'first_name' ? value : updatedLead.first_name;
        const lastName = field === 'last_name' ? value : updatedLead.last_name;
        updatedLead.customer_name = `${firstName || ''} ${lastName || ''}`.trim();
      }

      setSelectedLead(updatedLead);

      if (!addDialogOpen) {
        // For existing leads: update immediately
        updateLead(selectedLead.id, { [field]: value });
      }
      // For new leads: just update the form state, don't add until button click
    }
  };

  // Search for existing customers
  const searchCustomers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setCustomerOptions([]);
      return;
    }

    try {
      setLoadingCustomers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/customers?search=${searchTerm}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCustomerOptions(response.data.customers || []);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Handle customer selection from existing customers
  const handleCustomerSelect = (customer: any) => {
    if (!customer) {
      setSelectedCustomer(null);
      return;
    }

    setSelectedCustomer(customer);

    // Pre-fill form with customer data
    // Set source to "returning" and introducer to Gordon Admin (ID: 1)
    const updatedLead = {
      ...selectedLead,
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      customer_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
      email: customer.email || '',
      phone: customer.phone || '',
      home_phone: customer.home_phone || '',
      address_1: customer.address || '',
      address_2: customer.address_2 || '',
      address_3: customer.address_3 || '',
      town: customer.city || '',
      postcode: customer.postal_code || '',
      source: 'Returning',
      introducer_id: 1, // Gordon Admin
    };

    setSelectedLead(updatedLead as Lead);
  };

  const handleAddLead = () => {
    const newLead: Lead = {
      id: Math.max(...leads.map(l => l.id), 0) + 1,
      title: '',
      customer_name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      home_phone: '',
      address_1: '',
      address_2: '',
      address_3: '',
      town: '',
      postcode: '',
      source: 'Website',
      status: 'new',
      priority: 'medium',
      estimated_value: 0,
      notes: '',
      created_at: new Date().toISOString().split('T')[0],
      next_follow_up: '',
      introducer_id: null,
      actions: [],
    };
    setSelectedLead(newLead);
    setLeadMode('new');
    setSelectedCustomer(null);
    setCustomerOptions([]);
    setCustomerSearch('');
    setAddDialogOpen(true);
  };

  const handleSaveNewLead = async () => {
    if (selectedLead && selectedLead.first_name && selectedLead.last_name && selectedLead.title && (selectedLead.phone || selectedLead.home_phone)) {
      try {
        
        const leadData = {
          title: selectedLead.title,
          customer_first_name: selectedLead.first_name,
          customer_last_name: selectedLead.last_name,
          customer_email: selectedLead.email || '',
          customer_phone: selectedLead.phone || selectedLead.home_phone || '',
          customer_address: selectedLead.address_1 || '',
          customer_city: selectedLead.town || 'Bolton',
          customer_postal_code: selectedLead.postcode || '',
          customer_latitude: selectedLead.latitude || null,
          customer_longitude: selectedLead.longitude || null,
          source: selectedLead.source || 'Website',
          priority: selectedLead.priority || 'medium',
          estimated_value: selectedLead.estimated_value || 0,
          notes: selectedLead.notes || '',
          created_at: selectedLead.created_at || new Date().toISOString().split('T')[0],
          introducer_id: selectedLead.introducer_id || null,
        };


        const response = await fetch('/api/leads', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(leadData),
        });

        if (response.ok) {
          const result = await response.json();
          
          // Refresh both the local leads list and the context leads for the badge
          await fetchLeads();
          await refreshLeads();
          
          setAddDialogOpen(false);
          setSelectedLead(null);
        } else {
          const errorText = await response.text();
          console.error('Error saving lead:', response.status, errorText);
          alert('Error saving lead. Please try again.');
        }
      } catch (error) {
        console.error('Error saving lead:', error);
        alert('Error saving lead. Please try again.');
      }
    }
  };

  const handleAddAction = () => {
    if (selectedLead && newAction.description && newAction.action_type) {
      const action: LeadAction = {
        id: Date.now(), // Simple ID generation
        lead_id: selectedLead.id,
        action_type: newAction.action_type as LeadAction['action_type'],
        description: newAction.description,
        action_date: newAction.action_date!,
        action_time: newAction.action_time!,
        created_at: new Date().toISOString(),
      };

      // Add action to the lead via context
      addAction(selectedLead.id, action);
      
      // Update local selectedLead state to reflect the change
      const updatedLead = {
        ...selectedLead,
        actions: [...(selectedLead.actions || []), action]
      };
      setSelectedLead(updatedLead);

      // Reset the form
      setNewAction({
        action_type: 'Call',
        description: '',
        action_date: new Date().toISOString().split('T')[0],
        action_time: new Date().toTimeString().slice(0, 5),
      });
      setAddActionDialogOpen(false);
    }
  };

  const handleBookAppointment = async () => {
    if (selectedLead && appointment.date && appointment.time) {
      try {
        const assignedUserName = users.find(u => u.id === appointment.assignedUser)?.name || 'Unknown User';
        const token = localStorage.getItem('token');
        
        // 1. Create action in database
        const actionResponse = await fetch('/api/actions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            lead_id: selectedLead.id,
            customer_id: selectedLead.customer_id || null,
            action_type: 'Appointment Set',
            description: `Diary appointment set - ${appointment.type} on ${new Date(appointment.date).toLocaleDateString()} at ${appointment.time}. Assigned to: ${assignedUserName}${appointment.notes ? '. Notes: ' + appointment.notes : ''}`,
            action_date: new Date().toISOString().split('T')[0],
            action_time: new Date().toTimeString().slice(0, 5),
            completed: true
          }),
        });

        // 2. Create calendar event
        const calendarResponse = await fetch('/api/calendar/events', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: `${appointment.type} - ${selectedLead.customer_name || selectedLead.first_name + ' ' + selectedLead.last_name}`,
            description: appointment.notes || `${appointment.type} appointment`,
            event_type: 'appointment',
            event_date: appointment.date,
            start_time: appointment.time,
            end_time: null, // Let system set default
            all_day: false,
            location: selectedLead.address_1 ? `${selectedLead.address_1}, ${selectedLead.town || ''}, ${selectedLead.postcode || ''}`.trim() : null,
            lead_id: selectedLead.id,
            customer_id: selectedLead.customer_id || null,
            assigned_to: parseInt(appointment.assignedUser) || null,  // Convert string ID to number
            color: '#2196F3'
          }),
        });

        if (actionResponse.ok && calendarResponse.ok) {
          
          // 3. Update lead status to contacted
          await fetch(`/api/leads/${selectedLead.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...selectedLead,
              status: 'contacted'
            }),
          });

          // Refresh leads data to show the new action and status
          await fetchLeads();
          await refreshLeads();
          
          // Reset the appointment form and close dialog
          setAppointment({
            date: '',
            time: '',
            notes: '',
            type: 'Home Visit',
            assignedUser: '21'  // Tony Gordon's database ID
          });
          setAppointmentDialogOpen(false);
        } else {
          const actionError = actionResponse.ok ? null : await actionResponse.json();
          const calendarError = calendarResponse.ok ? null : await calendarResponse.json();
          console.error('Failed to create appointment:', { actionError, calendarError });
          alert('Failed to book appointment. Please try again.');
        }
      } catch (error) {
        console.error('Error booking appointment:', error);
        alert('Error booking appointment. Please try again.');
      }
    }
  };

  const getActionTypeColor = (actionType: string) => {
    const colors: { [key: string]: string } = {
      'Call': '#2196f3',
      'Quote': '#ff9800',
      'Appointment Set': '#9c27b0',
      'Visit Booked': '#3f51b5',
      'Won': '#4caf50',
      'Lost': '#f44336',
      'Update': '#795548',
    };
    return colors[actionType] || '#757575';
  };

  // Generate last 12 months options
  const getMonthOptions = () => {
    const options = [];
    // Add "All" option first
    options.push({ value: 'all', label: 'All' });
    
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  // Helper functions for summary cards
  const getSelectedMonthLeads = () => {
    return leads.filter(lead => {
      const leadDate = new Date(lead.created_at);
      // Month and year filters work independently
      const matchesMonth = selectedMonth === 0 || leadDate.getMonth() === (selectedMonth - 1);
      const matchesYear = selectedYear === 0 || leadDate.getFullYear() === selectedYear;
      return matchesMonth && matchesYear;
    });
  };

  const getNewLeadsThisMonth = () => {
    return getSelectedMonthLeads().length; // All leads in the selected month
  };

  const getQuotedLeadsThisMonth = () => {
    const selectedMonthLeads = getSelectedMonthLeads();
    return selectedMonthLeads.filter(lead => lead.status === 'quoted').length;
  };

  const getWonLeadsThisMonth = () => {
    const selectedMonthLeads = getSelectedMonthLeads();
    return selectedMonthLeads.filter(lead => lead.status === 'won').length;
  };

  const getWonValueThisMonth = () => {
    const selectedMonthLeads = getSelectedMonthLeads();
    return selectedMonthLeads
      .filter(lead => lead.status === 'won')
      .reduce((sum, lead) => sum + lead.estimated_value, 0);
  };

  const getPipelineValueThisMonth = () => {
    const selectedMonthLeads = getSelectedMonthLeads();
    return selectedMonthLeads
      .filter(lead => lead.status === 'new' || lead.status === 'contacted' || lead.status === 'quoted')
      .reduce((sum, lead) => sum + lead.estimated_value, 0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToCSV = () => {
    const selectedMonthLeads = getSelectedMonthLeads();
    let monthName;
    if (selectedMonth === 0 && selectedYear === 0) {
      monthName = 'All_Leads';
    } else if (selectedMonth === 0) {
      monthName = `All_Leads_${selectedYear}`;
    } else if (selectedYear === 0) {
      monthName = new Date(2000, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long' }) + '_All_Years';
    } else {
      monthName = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    
    // CSV headers
    const headers = [
      'Customer Name',
      'Lead Title', 
      'Email',
      'Phone',
      'Home Phone',
      'Address Line 1',
      'Address Line 2',
      'Address Line 3',
      'Town',
      'Postcode',
      'Source',
      'Status',
      'Priority',
      'Estimated Value',
      'Created At',
      'Next Follow-up',
      'Actions Count'
    ];
    
    // Convert leads to CSV rows
    const csvData = selectedMonthLeads.map(lead => [
      lead.customer_name,
      lead.title,
      lead.email || '',
      lead.phone || '',
      lead.home_phone || '',
      lead.address_1 || '',
      lead.address_2 || '',
      lead.address_3 || '',
      lead.town || '',
      lead.postcode,
      lead.source,
      lead.status,
      lead.priority,
      lead.estimated_value,
      lead.created_at,
      lead.next_follow_up,
      lead.actions?.length || 0
    ]);
    
    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Gordons_Makeovers_Leads_${monthName.replace(' ', '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading leads...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'flex-end',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2
      }}>
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedMonth}
              onChange={(e) => {
                const newMonth = Number(e.target.value);
                setSelectedMonth(newMonth);
                sessionStorage.setItem('leadFilterMonth', newMonth.toString());
              }}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              <MenuItem value={0}>All Months</MenuItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedYear}
              onChange={(e) => {
                const newYear = Number(e.target.value);
                setSelectedYear(newYear);
                sessionStorage.setItem('leadFilterYear', newYear.toString());
              }}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              <MenuItem value={0}>All Years</MenuItem>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i + 1).map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            '& > button': {
              flex: { xs: 1, sm: 'initial' }
            }
          }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddLead}
              fullWidth
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Add Lead
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={exportToCSV}
              fullWidth
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Export
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Search Box - Mobile Only at Top */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search leads..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Cards - Status KPIs */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(3, 1fr)',
          sm: 'repeat(4, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: 'repeat(8, 1fr)'
        },
        gap: { xs: 1, sm: 1.5, md: 2 },
        mb: 2.5
      }}>
        {/* New Leads - Keep this one first */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('new')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              New
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'new').length}
            </Typography>
          </CardContent>
        </Card>

        {/* No Reply */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('no_reply')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              No Reply
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, color: '#9e9e9e' }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'no_reply').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Not Ready */}
        <Card
          sx={{ 
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('not_ready')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Not Ready
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'not_ready').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Contacted */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('contacted')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Contacted
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, color: '#3498DB' }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'contacted').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Quoted */}
        <Card
          sx={{ 
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('quoted')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Quoted
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'quoted').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Won */}
        <Card
          sx={{ 
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('won')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Won
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'won').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Lost */}
        <Card
          sx={{ 
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('lost')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Lost
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="error.main" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {getSelectedMonthLeads().filter(lead => lead.status === 'lost').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Total Leads */}
        <Card
          sx={{ 
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('all')}
        >
          <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 1.5 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
              Total
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
              {getSelectedMonthLeads().length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            alignItems: { md: 'center' } 
          }}>
            {/* Search Box - Desktop Only */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' }, display: { xs: 'none', md: 'block' } }}>
              <TextField
                fullWidth
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 20%' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 20%' } }}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  label="Source"
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  {sourceOptions.map(source => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 20%' }, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {filteredLeads.length} leads found
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 600, sm: 750, md: 900 } }}>
          <TableHead>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('customer_name')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Customer
                  {sortField === 'customer_name' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('postcode')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', sm: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Postcode
                  {sortField === 'postcode' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('title')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', md: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Lead Title
                  {sortField === 'title' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('source')}
                sx={{
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', lg: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Source
                  {sortField === 'source' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('status')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('estimated_value')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', md: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Est. Value
                  {sortField === 'estimated_value' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('next_follow_up')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', lg: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Next Follow-up
                  {sortField === 'next_follow_up' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLeads
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((lead) => (
              <TableRow 
                key={lead.id}
                onClick={() => handleLeadClick(lead)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {lead.customer_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {lead.phone}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {lead.postcode}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {lead.title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Created: {new Date(lead.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {lead.source ? lead.source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Chip
                      label={lead.status.charAt(0).toUpperCase() + lead.status.slice(1).replace('_', ' ')}
                      color={getStatusColor(lead.status)}
                      size="small"
                    />
                    {lead.status === 'lost' && lead.lost_status && (
                      <Chip
                        label={lead.lost_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '0.7rem', mt: 0.5 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{formatCurrency(lead.estimated_value)}</TableCell>
                <TableCell>
                  {lead.next_follow_up && lead.next_follow_up !== 'null' && lead.next_follow_up !== '' ? (
                    <Typography 
                      variant="body2" 
                      color={isOverdue(lead.next_follow_up) ? 'error' : 'textPrimary'}
                    >
                      {new Date(lead.next_follow_up).toLocaleDateString()}
                      {isOverdue(lead.next_follow_up) && ' (Overdue)'}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {/* Empty space for no follow-up date */}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    title="Visit Done"
                    onClick={(e) => handleVisitClick(lead, e)}
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Phone call made"
                    onClick={(e) => handlePhoneClick(lead, e)}
                  >
                    <Phone />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Book an appointment"
                    onClick={(e) => handleCalendarClick(lead, e)}
                  >
                    <CalendarToday />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    title="Delete lead"
                    onClick={(e) => handleDeleteClick(lead, e)}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredLeads.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>


      {/* Add Lead Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Add New Lead
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Toggle between New and Existing Customer */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ToggleButtonGroup
                value={leadMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setLeadMode(newMode);
                    if (newMode === 'new') {
                      // Reset to empty form
                      const emptyLead: Lead = {
                        ...selectedLead!,
                        first_name: '',
                        last_name: '',
                        customer_name: '',
                        email: '',
                        phone: '',
                        home_phone: '',
                        address_1: '',
                        address_2: '',
                        address_3: '',
                        town: '',
                        postcode: '',
                      };
                      setSelectedLead(emptyLead);
                      setSelectedCustomer(null);
                    }
                  }
                }}
                aria-label="lead mode"
              >
                <ToggleButton value="new" aria-label="new customer">
                  New Customer
                </ToggleButton>
                <ToggleButton value="existing" aria-label="existing customer">
                  Existing Customer
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Existing Customer Search */}
            {leadMode === 'existing' && (
              <Autocomplete
                options={customerOptions}
                loading={loadingCustomers}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} - ${option.phone || option.email || ''}`}
                onInputChange={(event, newValue) => {
                  setCustomerSearch(newValue);
                  searchCustomers(newValue);
                }}
                onChange={(event, newValue) => handleCustomerSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Existing Customer"
                    placeholder="Type name, phone, or email..."
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Lead Title"
                value={selectedLead?.title || ''}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                variant="outlined"
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="First Name"
                value={selectedLead?.first_name || ''}
                onChange={(e) => handleFieldChange('first_name', e.target.value)}
                variant="outlined"
                required
                disabled={leadMode === 'existing' && selectedCustomer !== null}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={selectedLead?.last_name || ''}
                onChange={(e) => handleFieldChange('last_name', e.target.value)}
                variant="outlined"
                required
                disabled={leadMode === 'existing' && selectedCustomer !== null}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedLead?.status || 'new'}
                  onChange={(e) => {
                    handleFieldChange('status', e.target.value);
                    if (e.target.value !== 'lost') {
                      handleFieldChange('lost_status', undefined);
                    }
                  }}
                  label="Status"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedLead?.status === 'lost' && (
                <FormControl fullWidth>
                  <InputLabel>Lost Reason</InputLabel>
                  <Select
                    value={selectedLead?.lost_status || ''}
                    onChange={(e) => handleFieldChange('lost_status', e.target.value)}
                    label="Lost Reason"
                  >
                    <MenuItem value="junk">Junk</MenuItem>
                    <MenuItem value="out_of_area">Out of Area</MenuItem>
                    <MenuItem value="new_spray">New/Spray</MenuItem>
                    <MenuItem value="no_reply">No Reply</MenuItem>
                    <MenuItem value="not_interested">Not Interested</MenuItem>
                    <MenuItem value="wrong_number">Wrong Number</MenuItem>
                    <MenuItem value="already_sorted">Already Sorted</MenuItem>
                    <MenuItem value="price">Price</MenuItem>
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                label="Estimated Value"
                type="number"
                value={selectedLead?.estimated_value || ''}
                onChange={(e) => handleFieldChange('estimated_value', parseFloat(e.target.value))}
                variant="outlined"
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
              {/* Address Section */}
              <Box sx={{ 
                flex: '1 1 50%', 
                border: '2px solid #e0e0e0', 
                borderRadius: 2, 
                p: 2,
                backgroundColor: '#fafafa'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>Address</Typography>
                  <Tooltip title="Search via Postcode" arrow>
                    <IconButton
                      onClick={handleOpenPostcodeLookup}
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      <Search fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={selectedLead?.address_1 || ''}
                    onChange={(e) => handleFieldChange('address_1', e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={leadMode === 'existing' && selectedCustomer !== null}
                  />
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={selectedLead?.address_2 || ''}
                    onChange={(e) => handleFieldChange('address_2', e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={leadMode === 'existing' && selectedCustomer !== null}
                  />
                  <TextField
                    fullWidth
                    label="Town"
                    value={selectedLead?.town || ''}
                    onChange={(e) => handleFieldChange('town', e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={leadMode === 'existing' && selectedCustomer !== null}
                  />
                  <TextField
                    fullWidth
                    label="Address Line 3"
                    value={selectedLead?.address_3 || ''}
                    onChange={(e) => handleFieldChange('address_3', e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={leadMode === 'existing' && selectedCustomer !== null}
                  />
                  <TextField
                    fullWidth
                    label="Postcode"
                    value={selectedLead?.postcode || ''}
                    onChange={(e) => handleFieldChange('postcode', e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={leadMode === 'existing' && selectedCustomer !== null}
                  />
                </Box>
              </Box>
              
              {/* Contact & Source Fields */}
              <Box sx={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Contact Details</Typography>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={selectedLead?.email || ''}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  variant="outlined"
                  disabled={leadMode === 'existing' && selectedCustomer !== null}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={selectedLead?.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  variant="outlined"
                  required
                  disabled={leadMode === 'existing' && selectedCustomer !== null}
                />
                <TextField
                  fullWidth
                  label="Home Phone"
                  value={selectedLead?.home_phone || ''}
                  onChange={(e) => handleFieldChange('home_phone', e.target.value)}
                  variant="outlined"
                  disabled={leadMode === 'existing' && selectedCustomer !== null}
                />
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={selectedLead?.source || 'Website'}
                    onChange={(e) => handleFieldChange('source', e.target.value)}
                    label="Source"
                  >
                    {sourceOptions.map(source => (
                      <MenuItem key={source} value={source}>{source}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Introducer</InputLabel>
                  <Select
                    value={selectedLead?.introducer_id || ''}
                    onChange={(e) => handleFieldChange('introducer_id', e.target.value ? Number(e.target.value) : null)}
                    label="Introducer"
                  >
                    <MenuItem value="">No Introducer</MenuItem>
                    {introducers.map(introducer => (
                      <MenuItem key={introducer.id} value={introducer.id}>
                        {introducer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Date Added"
                  type="date"
                  value={selectedLead?.created_at || new Date().toISOString().split('T')[0]}
                  onChange={(e) => handleFieldChange('created_at', e.target.value)}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  helperText="You can backdate this lead if it was received earlier"
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={4}
              variant="outlined"
              placeholder="Add any additional notes about this lead..."
              value={selectedLead?.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNewLead} 
            variant="contained" 
            color="primary"
            disabled={!selectedLead?.first_name || !selectedLead?.last_name || !selectedLead?.title || (!selectedLead?.phone && !selectedLead?.home_phone)}
          >
            Add Lead
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Action Dialog */}
      <Dialog open={addActionDialogOpen} onClose={() => setAddActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Action for {selectedLead?.customer_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={newAction.action_type || 'Call'}
                onChange={(e) => setNewAction({ ...newAction, action_type: e.target.value as LeadAction['action_type'] })}
                label="Action Type"
              >
                {actionTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={newAction.action_date || ''}
                onChange={(e) => setNewAction({ ...newAction, action_date: e.target.value })}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={newAction.action_time || ''}
                onChange={(e) => setNewAction({ ...newAction, action_time: e.target.value })}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newAction.description || ''}
              onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
              variant="outlined"
              placeholder="Describe what happened during this action..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddActionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAction} variant="contained" color="primary">
            Add Action
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Booking Dialog */}
      <Dialog open={appointmentDialogOpen} onClose={() => setAppointmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Book Appointment for {selectedLead?.customer_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={appointment.type}
                  onChange={(e) => setAppointment({ ...appointment, type: e.target.value })}
                  label="Appointment Type"
                >
                  <MenuItem value="Home Visit">Home Visit</MenuItem>
                  <MenuItem value="Phone Call">Phone Call</MenuItem>
                  <MenuItem value="Showroom Visit">Showroom Visit</MenuItem>
                  <MenuItem value="Follow-up">Follow-up</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={appointment.assignedUser}
                  onChange={(e) => setAppointment({ ...appointment, assignedUser: e.target.value })}
                  label="Assigned To"
                >
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Appointment Date"
                type="date"
                value={appointment.date}
                onChange={(e) => setAppointment({ ...appointment, date: e.target.value })}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={appointment.time}
                onChange={(e) => setAppointment({ ...appointment, time: e.target.value })}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={appointment.notes}
              onChange={(e) => setAppointment({ ...appointment, notes: e.target.value })}
              variant="outlined"
              placeholder="Additional notes about the appointment..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppointmentDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBookAppointment} 
            variant="contained" 
            color="primary"
            disabled={!appointment.date || !appointment.time}
          >
            Book Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Postcode Lookup Dialog */}
      <Dialog open={postcodeDialogOpen} onClose={() => setPostcodeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Address Lookup
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Enter a postcode to search for addresses
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                label="Postcode"
                placeholder="e.g. PR1 5TG"
                value={postcodeSearch}
                onChange={(e) => setPostcodeSearch(e.target.value)}
                variant="outlined"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePostcodeLookup();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handlePostcodeLookup}
                disabled={loadingAddresses || !postcodeSearch.trim()}
                sx={{ minWidth: 120 }}
              >
                {loadingAddresses ? 'Searching...' : 'Search'}
              </Button>
            </Box>
            
            {addressOptions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Found {addressOptions.length} addresses:
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {addressOptions.map((address, index) => (
                    <Card
                      key={index}
                      sx={{
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                      onClick={() => handleAddressSelect(address)}
                    >
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {address.address1}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {address.postTown}, {address.county} {address.postcode}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostcodeDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Call Action Dialog */}
      <Dialog 
        open={quickCallDialogOpen} 
        onClose={() => setQuickCallDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Record Phone Call
          {quickCallLead && (
            <Typography variant="body2" color="textSecondary">
              with {quickCallLead.customer_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip 
                icon={<Phone />}
                label="Call" 
                color="primary" 
                variant="filled"
              />
              <Typography variant="body2" color="textSecondary">
                {new Date().toLocaleDateString()} at {new Date().toTimeString().slice(0, 5)}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Call Description"
              placeholder="Enter details about this phone call..."
              value={quickCallDescription}
              onChange={(e) => setQuickCallDescription(e.target.value)}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setQuickCallDialogOpen(false);
              setQuickCallDescription('');
              setQuickCallLead(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleQuickCallSave}
            disabled={!quickCallDescription.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this lead?
          </Typography>
          {leadToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Customer:</strong> {leadToDelete.customer_name}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Title:</strong> {leadToDelete.title}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Status:</strong> {leadToDelete.status}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Value:</strong> £{leadToDelete.estimated_value}
              </Typography>
            </Box>
          )}
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setLeadToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete Lead
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}